"use client";

import { CSSProperties, ReactNode } from "react";
import styles from "./SizeableText.module.css";

export interface SizeableTextProps {
  children: ReactNode;
  size?: "xsmall" | "small" | "medium" | "large" | "xlarge";
  color?:
    | "primary"
    | "secondary"
    | "accent"
    | "error"
    | "info"
    | "warning"
    | "success"
    | "white";
  fontWeight?: "light" | "medium" | "strong" | "xstrong";
  fontStyle?: "normal" | "italic" | "oblique";
  textTransform?: "uppercase" | "lowercase" | "capitalize" | "none";
  textAlign?: "left" | "center" | "right" | "justify";
  letterSpacing?: string | number;
  lineHeight?: string | number;
  variant?: "p" | "span" | "div" | "h1" | "h2" | "h3" | "h4" | "h5" | "h6";
  className?: string;
  padding?: number;
  style?: CSSProperties;
}

export default function SizeableText({
  children,
  size = "medium",
  color,
  fontWeight,
  fontStyle,
  textTransform,
  textAlign,
  letterSpacing,
  lineHeight,
  variant = "p",
  className,
  padding,
  style,
}: SizeableTextProps) {
  const classNames = [
    styles.sizeableText,
    styles[size],
    color ? styles[color] : null,
    fontWeight && styles[`fontWeight-${fontWeight}`],
    fontStyle && styles[`fontStyle-${fontStyle}`],
    textTransform && styles[`textTransform-${textTransform}`],
    className,
  ]
    .filter(Boolean)
    .join(" ");

  let inlineStyles: CSSProperties = {};
  if (padding) {
    inlineStyles.padding = `var(--spacing-${padding})`;
  }

  const Component = variant;

  if (
    letterSpacing !== undefined ||
    textAlign !== undefined ||
    lineHeight !== undefined
  ) {
    inlineStyles = {
      ...inlineStyles,
      ...(letterSpacing !== undefined && {
        letterSpacing:
          typeof letterSpacing === "number"
            ? `${letterSpacing}px`
            : letterSpacing,
      }),
      ...(textAlign !== undefined && { textAlign }),
      ...(lineHeight !== undefined && {
        lineHeight: typeof lineHeight === "number" ? lineHeight : lineHeight,
      }),
    };
  }

  const finalStyles = style ? { ...inlineStyles, ...style } : inlineStyles;

  return (
    <Component className={classNames} style={finalStyles}>
      {children}
    </Component>
  );
}
